import { useTranslation } from 'react-i18next';

import { ModalComponent } from '@Components';
import { TrackedButton } from '@Components/ui-v2/tracked-button';
import { ReplacementPreview } from '@Models';

interface ReplacementPreviewModalProps {
  isOpen: boolean;
  masterTitle: string;
  fileName: string;
  preview: ReplacementPreview | null;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * What the upload would change, before it replaces anything.
 *
 * Shown for every replacement, including one that changes nothing: that
 * it changes nothing is itself worth reading, because it usually means
 * the wrong file was picked.
 */
const ReplacementPreviewModal = ({
  isOpen,
  masterTitle,
  fileName,
  preview,
  onCancel,
  onConfirm,
}: ReplacementPreviewModalProps) => {
  const { t } = useTranslation();

  if (!preview) return null;

  const columnsChanged = preview.columns_added.length > 0 || preview.columns_deleted.length > 0;
  const rowsChanged = preview.rows_added > 0 || preview.rows_removed > 0;

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={onCancel}
      title={t('Configuration.PreviewTitle')}
      closeClickOutside={false}>
      <div className="w-full flex flex-col gap-4">
        <p className="text-sm text-gray-600">
          {t('Configuration.PreviewMessage', { master: masterTitle, file: fileName })}
        </p>

        {columnsChanged && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
            <p className="text-sm font-semibold text-amber-900 flex items-start gap-2">
              {/* Outside the translation so one sign serves both languages. */}
              <span aria-hidden="true">⚠️</span>
              <span>{t('Configuration.PreviewColumnsWarning')}</span>
            </p>
            {preview.columns_added.length > 0 && (
              <p className="text-sm text-amber-800 mt-1 break-words pl-6">
                <span className="font-semibold">+</span> {preview.columns_added.join(', ')}
              </p>
            )}
            {preview.columns_deleted.length > 0 && (
              <p className="text-sm text-amber-800 mt-1 break-words pl-6">
                <span className="font-semibold">−</span> {preview.columns_deleted.join(', ')}
              </p>
            )}
          </div>
        )}

        <dl className="w-full rounded-lg border border-gray-200 divide-y divide-gray-200">
          <div className="flex justify-between gap-4 px-4 py-3">
            <dt className="text-sm text-gray-500">{t('Configuration.PreviewRows')}</dt>
            <dd className="text-sm text-gray-900 text-right tabular-nums">
              <span className="text-green-700 font-semibold">+{preview.rows_added}</span>{' '}
              <span className="text-red-700 font-semibold">−{preview.rows_removed}</span>
            </dd>
          </div>
          <div className="flex justify-between gap-4 px-4 py-3">
            <dt className="text-sm text-gray-500">{t('Configuration.PreviewColumns')}</dt>
            <dd className="text-sm text-gray-900 text-right tabular-nums">
              <span className="text-green-700 font-semibold">+{preview.columns_added.length}</span>{' '}
              <span className="text-red-700 font-semibold">−{preview.columns_deleted.length}</span>
            </dd>
          </div>
        </dl>

        {!columnsChanged && !rowsChanged && (
          <p className="text-sm text-gray-500">{t('Configuration.PreviewNoChanges')}</p>
        )}

        <div className="flex gap-3 items-center w-full justify-center">
          <TrackedButton
            variant="outline"
            buttonName="CONFIGURATION_FILE_REPLACE_CANCELED"
            onClick={onCancel}
            isSubmit={false}
            className="w-full bg-white hover:border-bg-gray-300 hover:bg-gray-50 cursor-pointer hover:shadow-sm">
            <span className="flex items-center justify-center">{t('Configuration.Cancel')}</span>
          </TrackedButton>
          <TrackedButton
            buttonName="CONFIGURATION_FILE_REPLACE_CONFIRMED"
            isSubmit={false}
            onClick={onConfirm}
            className="w-full">
            <span className="flex items-center justify-center">{t('Configuration.ConfirmReplace')}</span>
          </TrackedButton>
        </div>
      </div>
    </ModalComponent>
  );
};

export default ReplacementPreviewModal;
